import React, { useEffect, useReducer, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useAccountCompanyId } from 'application/hooks';
import { TEXTS } from 'application/pages/BillingPage/renderer/texts';
import { ModalTypes } from 'application/providers/ModalProvider';
import { BILLING_PAGE_PATH, INTEGRATION_PAGE_PATH, POINTS_MENU_PATH } from 'application/consts';
import {
  NOTIFICATION_DEFAULT_ERROR_MESSAGE,
  NotificationContextType,
} from 'application/providers/NotificationProvider';
import AnalyticsService, { EventNameAMP } from 'application/services/AnalyticsService';
import { NavigateFunction, RouterUtils } from 'application/providers/RouterProvider';
import { getStreamsCategoryPath, StreamsCategoryParamValues } from 'application/pages/StreamPage';
import { CurrentPointOnboardingType, SelectPlayerFlowType } from 'application/components/common/SelectPlayerFlow/types';
import { startAcceptanceAdvancedSigning } from 'application/store';

import { SubscriptionStatus } from 'domain/billing/types';

import { AcceptanceAdvanceStatus, PaymentKind } from 'domain/api/graphql/generated';
import { LicensesModalState, UseLicensesModalViewModel } from './types';
import {
  CURRENT_POINT_KEY,
  DEFAULT_LICENSES_MODAL_STATE,
  IS_NEED_REDIRECT_TO_SOURCE_FROM_KEY,
  IS_NEED_SHOW_LICENSES_MODAL,
  PERSISTED_LICENSES_KEY,
  ONBOARDING_FLOW,
} from './consts';
import { licensesModalReducer } from './reducer';
import { StateParamValues } from '../..';
import { persistCurrentPoint, persistToEnableLicenses, sendEvent } from './utils';
import { useBillingData } from './useBillingData';

interface Props {
  notification: NotificationContextType;
  closeModalButton: React.ReactNode;
  unpaidLicensesOnly: boolean;
  isViewMode: boolean;
  flowType?: SelectPlayerFlowType;
  currentPoint: CurrentPointOnboardingType;
  openModal?: (type: ModalTypes, props: {}) => void;
  close: (count?: number) => void;
  navigate: NavigateFunction;
  setCurrentPoint: (currentPoint: CurrentPointOnboardingType) => void;
  clearCurrentPoint: () => void;
}

const BILLING_PAGE_INFO_PATH = RouterUtils.generatePath(BILLING_PAGE_PATH, { state: StateParamValues.INFO });
const URI_FOR_RETURN_CARD = `${window.location.origin}/${BILLING_PAGE_INFO_PATH}`;
const URI_FOR_RETURN_PAYMENT = `${window.location.origin}/${RouterUtils.generatePath(INTEGRATION_PAGE_PATH, { type: 'sbbol' })}`;

const useLicensesModalViewModel = ({
  notification,
  closeModalButton,
  unpaidLicensesOnly,
  isViewMode,
  currentPoint,
  openModal,
  close,
  navigate,
  setCurrentPoint,
  clearCurrentPoint,
}: Props): UseLicensesModalViewModel | null => {
  const storeDispatch = useDispatch();

  const [state, dispatch] = useReducer(licensesModalReducer, {
    ...DEFAULT_LICENSES_MODAL_STATE,
    isViewMode,
  });

  const isInitialized = useRef<boolean>(false);
  const isSetUnpaidAsToEnable = useRef<boolean>(false);

  const companyId = useAccountCompanyId();

  const billingData = useBillingData(companyId);

  useEffect(() => {
    if (!billingData) return;

    if (isSetUnpaidAsToEnable.current) return;

    if (unpaidLicensesOnly) {
      setToEnableAutoRenewalFromUnpaid();
      isSetUnpaidAsToEnable.current = true;
    }
  }, [billingData, isSetUnpaidAsToEnable.current]);

  useEffect(() => {
    if (!billingData) return;

    if (isInitialized.current) return;

    const { hasInactiveLicenses, hasActiveLicenses, unpaidLicensesIds } = billingData;

    const initializeState = async () => {
      const restored = await restoreState();

      const currentPointString = localStorage.getItem(CURRENT_POINT_KEY);
      const hasCurrentPoint = currentPointString && currentPointString !== 'undefined';

      if (!restored && (hasInactiveLicenses || hasActiveLicenses) && hasCurrentPoint) {
        dispatch({ type: 'setToEnableAutorenewal', toEnableAutorenewal: unpaidLicensesIds });

        if (hasActiveLicenses) {
          await setExtendSubscriptionCost(unpaidLicensesIds);
        }
      }
    };

    initializeState();

    isInitialized.current = true;
  }, [billingData, isInitialized.current]);

  if (!billingData) return null;

  const {
    paymentMethod,
    isLinkingInProcess,
    status,
    hasTrialLicenses,
    hasInactiveLicenses,
    hasActiveLicenses,
    hasWaitPaymentLicenses,
    hasGraceLicenses,
    unpaidLicenses,
    nonRenewableLicenses,
    totalLicenses,
    unpaidLicensesIds,
    selectedPaymentKind,
    acceptanceAdvanceStatus,

    getLinkForBindingCard,
    getLinkForPayment,
    requestCost,
    subscriptionCostLoading,
    suspendAutoRenewal,
    suspendAutoRenewalResult,
    resumeAutoRenewal,
    resumeAutoRenewalResult,
    refetchBillingInfo,
  } = billingData;

  const toEnableAutoRenewalPaid = state.toEnableAutorenewal.filter((id) =>
    nonRenewableLicenses.some((license) => license.id === id)
  );

  const toEnableAutoRenewalUnpaid = state.toEnableAutorenewal.filter((id) =>
    unpaidLicensesIds.some((licenseId) => licenseId === id)
  );

  const isNeedPayment = state.toEnableAutorenewal.length > 0 && state.licensesSubscriptionCost !== 0;

  const bindCardVisible =
    selectedPaymentKind === PaymentKind.Yookassa &&
    hasActiveLicenses &&
    !paymentMethod &&
    !isLinkingInProcess &&
    toEnableAutoRenewalUnpaid.length > 0;

  const isPendingAcceptanceAdvanced =
    selectedPaymentKind === PaymentKind.Sbbol &&
    acceptanceAdvanceStatus === AcceptanceAdvanceStatus.Pending &&
    !hasTrialLicenses;

  const isSubmitUnpaidButtonDisabled =
    state.toEnableAutorenewal.length === 0 ||
    bindCardVisible ||
    (isLinkingInProcess && selectedPaymentKind === PaymentKind.Yookassa) ||
    isPendingAcceptanceAdvanced;

  const isHintShow = state.toSuspendAutorenewal.length === 0 && state.toEnableAutorenewal.length === 0;

  const isSubmitButtonDisabled =
    isHintShow ||
    bindCardVisible ||
    (isLinkingInProcess && selectedPaymentKind === PaymentKind.Yookassa) ||
    isPendingAcceptanceAdvanced;

  const isChangeAutoRenewalButtonDisabled = unpaidLicensesOnly ? isSubmitUnpaidButtonDisabled : isSubmitButtonDisabled;

  const resumeSubscriptionCost = async (count: number) => {
    const costResult = await requestCost.resumeCost(companyId, count);

    const resumeCost = costResult?.costWithDiscount || costResult?.cost || undefined;

    return status !== SubscriptionStatus.TrialSubscription ? resumeCost || 0 : 0;
  };

  const extendSubscriptionCost = async (count: number) => {
    const costResult = await requestCost.extendCost(companyId, count);

    return costResult || 0;
  };

  const getSubscriptionCost = async (licensesIds: string[]) => {
    const ids = licensesIds.filter((id) => unpaidLicenses.find((license) => license.id === id));

    if (hasInactiveLicenses) {
      const cost = await resumeSubscriptionCost(ids.length);

      return cost;
    }

    const cost = await extendSubscriptionCost(ids.length);

    return cost;
  };

  const redirectToBindCard = (link?: string) => {
    AnalyticsService.event(EventNameAMP.BIND_CARD_PRESSED);

    if (link) {
      window.location.assign(link);
    } else {
      notification.showErrorNotification({
        children: TEXTS.BILLING_BIND_CARD_NOTIFICATION_ERROR_MESSAGE,
      });

      localStorage.removeItem(IS_NEED_SHOW_LICENSES_MODAL);
    }
  };

  const bindCard = async () => {
    const link = await getLinkForBindingCard(URI_FOR_RETURN_CARD, companyId);

    redirectToBindCard(link);
  };

  const bindCardAndPayConfirm = async (licensesIds: string[]) => {
    const link = await getLinkForPayment(URI_FOR_RETURN_CARD, companyId, licensesIds);

    redirectToBindCard(link);
  };

  const changeAutoRenewal = async () => {
    if (state.toSuspendAutorenewal.length > 0) {
      await suspendAutoRenewal(companyId, state.toSuspendAutorenewal);
    }

    if (state.toEnableAutorenewal.length > 0) {
      await resumeAutoRenewal(companyId, toEnableAutoRenewalPaid);
    }

    await refetchBillingInfo();

    const isNeedStopSubscription = toEnableAutoRenewalUnpaid.length === 0 && state.toSuspendAutorenewal.length > 0;

    const isNeedStartSubscription =
      toEnableAutoRenewalUnpaid.length === 0 &&
      toEnableAutoRenewalPaid.length > 0 &&
      state.toSuspendAutorenewal.length === 0;

    if (isNeedStopSubscription) {
      openModal?.(ModalTypes.CUSTOM, {
        title: TEXTS.BILLING_AUTORENEWAL_SUSPENDED_TITLE,
        description: TEXTS.BILLING_AUTORENEWAL_SUSPENDED_DESC,
        actionButtons: [closeModalButton],
      });
    }

    if (isNeedStartSubscription) {
      openModal?.(ModalTypes.CUSTOM, {
        title: TEXTS.BILLING_SUBMIT_CHANGE_AUTORENEWABLE_TITLE,
        description: TEXTS.BILLING_SUBMIT_CHANGE_AUTORENEWABLE_SUCCESS,
        actionButtons: [closeModalButton],
      });

      if (hasTrialLicenses) {
        notification.showInfoNotification({
          children: TEXTS.BILLING_SUBMIT_CHANGE_AUTORENEWABLE_TRIAL,
        });
      }
    }
  };

  const redirectToSuccessStep = () => {
    const onboardingFlowType = localStorage.getItem(ONBOARDING_FLOW);
    localStorage.removeItem(ONBOARDING_FLOW);

    if (onboardingFlowType === SelectPlayerFlowType.CHANGE_DEVICE) {
      navigate(RouterUtils.generatePath(POINTS_MENU_PATH, {}, { success: 'true' }));

      return;
    }

    const streamPath = getStreamsCategoryPath(StreamsCategoryParamValues.RECOMMENDED);
    navigate(RouterUtils.generatePath(streamPath, {}, { success: 'true' }));
  };

  const checkIfNeedRedirectToSourceFrom = () => {
    const currentPointString = localStorage.getItem(CURRENT_POINT_KEY);

    if (currentPointString && currentPointString !== 'undefined') {
      const currentPoint = JSON.parse(currentPointString) as CurrentPointOnboardingType;
      setCurrentPoint({ ...currentPoint, isEnabled: true });
      redirectToSuccessStep();
      close(2);
    }

    localStorage.removeItem(IS_NEED_REDIRECT_TO_SOURCE_FROM_KEY);
    localStorage.removeItem(CURRENT_POINT_KEY);
  };

  const openConfirmPayModal = () => {
    openModal?.(ModalTypes.BILLING_EXTEND_SUBSCRIPTION, {
      licenses: toEnableAutoRenewalUnpaid,
      submitCallback: () => {
        notification.showInfoNotification({
          children:
            selectedPaymentKind === PaymentKind.Sbbol
              ? TEXTS.BILLING_AWAITING_SBBOL_TEXT
              : TEXTS.BILLING_AWAITING_CARD_TEXT,
        });

        checkIfNeedRedirectToSourceFrom();
      },
    });
  };

  const toPayUnpaidLicenses = async () => {
    try {
      if (
        selectedPaymentKind === PaymentKind.Sbbol &&
        (acceptanceAdvanceStatus === AcceptanceAdvanceStatus.Active ||
          acceptanceAdvanceStatus === AcceptanceAdvanceStatus.Authorized)
      ) {
        openConfirmPayModal();

        return;
      }

      if (!paymentMethod || hasWaitPaymentLicenses || hasGraceLicenses) {
        await bindCardAndPayConfirm(toEnableAutoRenewalUnpaid);
      } else {
        openConfirmPayModal();
      }
    } catch (e) {
      notification.showErrorNotification({
        children: NOTIFICATION_DEFAULT_ERROR_MESSAGE,
      });
    }
  };

  const handleBindCard = async () => {
    persistToEnableLicenses(state);

    await bindCard();
  };

  const handleLicensesModalClose = async () => {
    if (hasInactiveLicenses) {
      await resumeSubscriptionCost(unpaidLicenses.length);
    }

    clearCurrentPoint();
    localStorage.removeItem(IS_NEED_REDIRECT_TO_SOURCE_FROM_KEY);
    localStorage.removeItem(CURRENT_POINT_KEY);
    close();
  };

  const handleChangeAutoRenewal = async () => {
    sendEvent(unpaidLicensesOnly, isNeedPayment);

    try {
      await changeAutoRenewal();

      if (toEnableAutoRenewalUnpaid.length > 0) {
        await toPayUnpaidLicenses();
      }
    } catch (e) {
      notification.showErrorNotification({
        children: TEXTS.BILLING_SUBMIT_CHANGE_AUTORENEWABLE_ERROR,
      });
    }
  };

  const handleChangeAutoRenewalUnpaid = async () => {
    try {
      await changeAutoRenewal();

      if (hasInactiveLicenses && !paymentMethod && currentPoint && selectedPaymentKind === PaymentKind.Yookassa) {
        persistCurrentPoint(currentPoint);
      }

      await toPayUnpaidLicenses();
    } catch (e) {
      notification.showErrorNotification({
        children: TEXTS.BILLING_SUBMIT_CHANGE_AUTORENEWABLE_ERROR,
      });
    }
  };

  const setSubscriptionCost = async (licensesIds: string[]) => {
    if (licensesIds.length > 0) {
      const cost = await getSubscriptionCost(licensesIds);
      dispatch({ type: 'setLicensesSubscriptionCost', licensesSubscriptionCost: cost });
    }
  };

  const setEditMode = () => {
    dispatch({ type: 'setIsViewMode', isViewMode: false });
  };

  async function setExtendSubscriptionCost(licensesIds: string[]) {
    const cost = await requestCost.extendCost(companyId, licensesIds.length);
    dispatch({ type: 'setLicensesSubscriptionCost', licensesSubscriptionCost: cost || 0 });
  }

  async function setToEnableAutoRenewalFromUnpaid() {
    dispatch({ type: 'setToEnableAutorenewal', toEnableAutorenewal: unpaidLicensesIds });
    await setSubscriptionCost(unpaidLicensesIds);
  }

  async function restoreState() {
    const persistedStateString = localStorage.getItem(PERSISTED_LICENSES_KEY);

    if (persistedStateString && persistedStateString !== 'undefined') {
      const persistedState = JSON.parse(persistedStateString) as LicensesModalState;
      const unpaidIds = persistedState.toEnableAutorenewal.filter((id) =>
        unpaidLicenses.find((license) => license.id === id)
      );

      dispatch({ type: 'setToEnableAutorenewal', toEnableAutorenewal: persistedState.toEnableAutorenewal });
      dispatch({ type: 'setToSuspendAutorenewal', toSuspendAutorenewal: persistedState.toSuspendAutorenewal });
      await setExtendSubscriptionCost(unpaidIds);

      localStorage.removeItem(PERSISTED_LICENSES_KEY);
      localStorage.removeItem(IS_NEED_SHOW_LICENSES_MODAL);

      return true;
    }

    return false;
  }

  const openAcceptanceAdvancedSigning = () => {
    storeDispatch(
      startAcceptanceAdvancedSigning({
        companyId,
        uriForReturn: URI_FOR_RETURN_PAYMENT,
        fallbackFn: () => {
          openModal?.(ModalTypes.SBERBUSSINESS_OPEN, {});
        },
      })
    );
  };

  return {
    dispatch,
    viewMode:
      state.isViewMode &&
      (hasTrialLicenses || (hasActiveLicenses && unpaidLicenses.length === 0 && nonRenewableLicenses.length === 0)),
    toSuspendAutorenewal: state.toSuspendAutorenewal,
    toEnableAutorenewal: state.toEnableAutorenewal,
    isChangeAutoRenewalButtonDisabled,
    totalLicenses,
    unpaidLicenses,
    licensesSubscriptionCost: state.licensesSubscriptionCost,
    hasInactiveLicenses,
    unpaidLicensesIds,
    isNeedPayment,
    bindCardVisible,
    isLinkingInProcess: Boolean(isLinkingInProcess) && selectedPaymentKind === PaymentKind.Yookassa,
    isHintShow,
    isSubmitLoading: subscriptionCostLoading || suspendAutoRenewalResult.loading || resumeAutoRenewalResult.loading,
    isPendingAcceptanceAdvanced,
    setSubscriptionCost,
    handleBindCard,
    handleChangeAutoRenewal: unpaidLicensesOnly ? handleChangeAutoRenewalUnpaid : handleChangeAutoRenewal,
    handleLicensesModalClose,
    setEditMode,
    openAcceptanceAdvancedSigning,
  };
};

export default useLicensesModalViewModel;
